import {
  Box,
  Button,
  Grid,
  MenuItem,
  Radio,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState, type ChangeEvent, type FC, useMemo } from "react";
import { ActionItem } from "../MagicPage";
import { ImgUploadBtn } from "../buttons/ImgUploadBtn";
import { SizeInputType, isStrNumErr } from "../../utils/number.util";

type OriginActionsProps = {
  onResetClick: () => void;
  actions: ActionItem[];
  onReUploadClick: (event: ChangeEvent<HTMLInputElement>) => void;
  actionsDisabled: boolean;
  magicUrl: string;
  onRadioChange: () => void;
  radioValue: "origin" | "mask";
  disableFirstBtn: boolean;
  expectedMagicImgSize: { width: number; height: number };
  onSizeSetDoneClick: (sizeSet: { width: number; height: number }) => void;
  inShortcutFunc: boolean;
};

export const OriginActions: FC<OriginActionsProps> = (props) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const [actionType, setActionType] = useState(props.actions[0].label);

  const [sizeSet, setSizeSet] = useState<SizeInputType>({
    width: props.expectedMagicImgSize.width,
    height: props.expectedMagicImgSize.height,
  });

  // 点击重新编辑、继续编辑后
  useEffect(() => {
    if (!props.magicUrl) {
      setActionType(props.actions[0].label);
      setSizeSet({
        width: props.expectedMagicImgSize.width,
        height: props.expectedMagicImgSize.height,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.magicUrl]);

  // 点击重新编辑、继续编辑、重置后
  useEffect(() => {
    if (
      sizeSet.width !== props.expectedMagicImgSize.width ||
      sizeSet.height !== props.expectedMagicImgSize.height
    ) {
      setActionType(props.actions[0].label);
      setSizeSet({
        width: props.expectedMagicImgSize.width,
        height: props.expectedMagicImgSize.height,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.expectedMagicImgSize]);

  const isSizeSetWidthErr = useMemo(
    () => isStrNumErr(sizeSet.width),
    [sizeSet.width]
  );

  const isSizeSetHeightErr = useMemo(
    () => isStrNumErr(sizeSet.height),
    [sizeSet.height]
  );

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexWrap: notPhone ? "nowrap" : "wrap",
        gap: "8px",
        padding: "0 16px",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: "120px",
        }}
      >
        <Radio
          checked={props.radioValue === "origin"}
          onChange={props.onRadioChange}
          value="origin"
          disabled={props.disableFirstBtn}
          color={props.inShortcutFunc ? "secondary" : "primary"}
        />
        <Typography
          variant="body2"
          color={
            props.disableFirstBtn
              ? "textDisabled"
              : props.radioValue === "origin"
              ? props.inShortcutFunc
                ? "secondary"
                : "primary"
              : "textPrimary"
          }
          fontWeight={800}
        >
          原图操作
        </Typography>
      </Box>

      <TextField
        select
        label="原图操作"
        placeholder="原图操作"
        size="small"
        value={actionType}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const newActionType = e.target.value;
          if (newActionType === "调整尺寸") {
            setSizeSet({
              width: props.expectedMagicImgSize.width,
              height: props.expectedMagicImgSize.height,
            });
          }
          setActionType(e.target.value);
        }}
        sx={{ minWidth: "120px", fontWeight: 800 }}
        disabled={props.actionsDisabled}
        color={props.inShortcutFunc ? "secondary" : "primary"}
      >
        <MenuItem key={`origin-actions-type-size-setting`} value="调整尺寸">
          调整尺寸
        </MenuItem>
        {props.actions.map((act, idx) => (
          <MenuItem key={`origin-actions-type-${idx}`} value={act.label}>
            {act.label}
          </MenuItem>
        ))}
      </TextField>

      {actionType === "调整尺寸" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "0 8px",
          }}
        >
          <TextField
            label="宽（像素）"
            value={sizeSet.width}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSizeSet({
                width: e.target.value,
                height: sizeSet.height,
              })
            }
            placeholder="256 ~ 4096"
            size="small"
            type="number"
            error={isSizeSetWidthErr}
            helperText={isSizeSetWidthErr ? "256 ~ 4096" : ""}
            disabled={props.actionsDisabled}
            color={props.inShortcutFunc ? "secondary" : "primary"}
          />
          <TextField
            label="高（像素）"
            value={sizeSet.height}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSizeSet({
                width: sizeSet.width,
                height: e.target.value,
              })
            }
            placeholder="256 ~ 4096"
            size="small"
            type="number"
            error={isSizeSetHeightErr}
            helperText={isSizeSetHeightErr ? "256 ~ 4096" : ""}
            disabled={props.actionsDisabled}
            color={props.inShortcutFunc ? "secondary" : "primary"}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              props.onSizeSetDoneClick({
                width:
                  typeof sizeSet.width === "string"
                    ? parseInt(sizeSet.width)
                    : sizeSet.width,
                height:
                  typeof sizeSet.height === "string"
                    ? parseInt(sizeSet.height)
                    : sizeSet.height,
              });
            }}
            disabled={
              props.actionsDisabled ||
              isSizeSetWidthErr ||
              isSizeSetHeightErr ||
              (sizeSet.width === props.expectedMagicImgSize.width &&
                sizeSet.height === props.expectedMagicImgSize.height)
            }
            color={props.inShortcutFunc ? "secondary" : "primary"}
            sx={{ fontWeight: 800 }}
          >
            确定
          </Button>
        </Box>
      )}

      {props.actions
        .filter((act) => act.label === actionType)
        .map((act, idx) => (
          <Slider
            key={`origin-actions-slider-${idx}`}
            value={act.value}
            onChange={act.onChange}
            min={act.min ?? 0}
            max={act.max ?? 100}
            step={act.step ?? 1}
            size="small"
            disabled={props.actionsDisabled}
            color={props.inShortcutFunc ? "secondary" : "primary"}
          />
        ))}

      <ImgUploadBtn
        onImgUpload={props.onReUploadClick}
        label="重选"
        variant="contained"
        isFullWidth={false}
        sx={{ minWidth: "80px", marginTop: 0 }}
        size="small"
        disabled={props.actionsDisabled}
        color={props.inShortcutFunc ? "secondary" : "primary"}
      />

      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          props.onResetClick();

          setActionType(props.actions[0].label);
          setSizeSet({
            width: props.expectedMagicImgSize.width,
            height: props.expectedMagicImgSize.height,
          });
        }}
        disabled={props.actionsDisabled}
        sx={{ minWidth: "60px", fontWeight: 800 }}
        color={props.inShortcutFunc ? "secondary" : "primary"}
      >
        重置
      </Button>
    </Grid>
  );
};
